import React, { useRef, useState } from 'react';
import type { Settings } from 'react-slick';
import Slider from 'react-slick';
import { MDText } from 'i18n-react';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useWindowSize } from 'usehooks-ts';

import { Button, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from '@motorway/mw-highway-code';

import { SELLER_TYPES } from 'Components/VehicleList/VehicleListFilters.consts';
import {
	triggerMobilizeRowShowMoreClickedEvent,
	triggerMobilizeRowToggleEvent,
	triggerMobilizeRowVehicleCardClickedEvent,
} from 'Services/analytics/events/vehicleListEvents';
import { useSaleTimesFromStore } from 'Stores/SaleTimesStore/SaleTimesStore';
import { LIST_TYPES } from 'Utilities/consts';
import { useIsMobilizeMarketplaceVisible } from 'Utilities/hooks/useIsMobilizeMarketplaceVisible';

import { MobilizeVehicleCard } from './MobilizeVehicleCard/MobilizeVehicleCard';
import LocalTexts from './MobilizeRecommendationRow.texts';
import { useMobilizeRecommendations } from './useMobilizeRecommendations';

import 'slick-carousel/slick/slick.css';
import styles from './MobilizeRecommendationRow.module.scss';

const LocalT = new MDText(LocalTexts);

const LIMIT = 10;
const CONTENT_ID = 'mobilize-row-content';
const COLLAPSE_COOKIE = 'mobilizeRowCollapsed';

const CAROUSEL_SETTINGS: Settings = {
	arrows: false,
	dots: false,
	draggable: true,
	infinite: false,
	slidesToScroll: 1,
	swipeToSlide: true,
	variableWidth: true,
};

export interface MobilizeRecommendationRowProps {
	appliedFiltersCount: number;
	hasUserSetSort: boolean;
	listType: string;
}

export const MobilizeRecommendationRow: React.FC<MobilizeRecommendationRowProps> = ({
	appliedFiltersCount,
	hasUserSetSort,
	listType,
}) => {
	const isMobilizeMarketplaceVisible = useIsMobilizeMarketplaceVisible();
	const router = useRouter();
	const saleTimes = useSaleTimesFromStore();
	const sliderRef = useRef<Slider>(null);
	// Remount the carousel after a resize settles so variableWidth re-measures
	// (it caches slide widths and won't recompute on shrink otherwise).
	const { width: windowWidth = 0 } = useWindowSize({ debounceDelay: 150 });
	const [isExpanded, setIsExpanded] = useState(() => Cookies.get(COLLAPSE_COOKIE) !== 'true');
	const [currentSlide, setCurrentSlide] = useState(0);

	const meetsPreconditions =
		isMobilizeMarketplaceVisible && listType === LIST_TYPES.auction && appliedFiltersCount === 0 && !hasUserSetSort;

	const { error, vehicles } = useMobilizeRecommendations({
		enabled: meetsPreconditions,
		limit: LIMIT,
	});

	if (!meetsPreconditions || error || vehicles.length === 0) {
		return null;
	}

	const handleSeeAll = (): void => {
		triggerMobilizeRowShowMoreClickedEvent();
		void router.push({
			pathname: '/vehicle_list',
			query: { ...router.query, sellerType: SELLER_TYPES.FLEET },
		});
	};

	const handleToggleExpand = (): void => {
		setIsExpanded((prev) => {
			const next = !prev;
			triggerMobilizeRowToggleEvent(next);
			if (next) {
				Cookies.remove(COLLAPSE_COOKIE);
			} else {
				const nextSale = saleTimes?.nextSaleStartsAt;
				const expires = nextSale ? new Date(nextSale) : undefined;
				Cookies.set(COLLAPSE_COOKIE, 'true', expires ? { expires } : undefined);
			}
			return next;
		});
	};

	const handleBrandKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handleToggleExpand();
		}
	};

	const carouselSettings: Settings = {
		...CAROUSEL_SETTINGS,
		afterChange: setCurrentSlide,
	};

	const canGoPrev = currentSlide > 0;
	const canGoNext = currentSlide < vehicles.length - 1;
	const toggleLabel = isExpanded ? LocalT.translate('collapseToggleLabel') : LocalT.translate('expandToggleLabel');

	return (
		<section className={styles.container} data-cy="mobilizeRecommendationRow">
			<div className={styles.headerRow}>
				<div
					aria-controls={CONTENT_ID}
					aria-expanded={isExpanded}
					className={styles.brand}
					onClick={handleToggleExpand}
					onKeyDown={handleBrandKeyDown}
					role="button"
					tabIndex={0}
				>
					<span className={styles.logo}>
						<img alt={`${LocalT.translate('logoAlt')}`} src="/static/images/mobilize-short-logo.png" />
					</span>
					<div className={styles.headerText}>
						<h2 className={styles.title}>{LocalT.translate('title')}</h2>
						<p className={styles.subtitle}>{LocalT.translate('subtitle')}</p>
					</div>
				</div>
				<div className={styles.headerActions}>
					<Button label={`${LocalT.translate('seeAllLabel')}`} onClick={handleSeeAll} size="small" />
					<div className={styles.collapseButton}>
						<Button
							aria-controls={CONTENT_ID}
							aria-expanded={isExpanded}
							aria-label={`${toggleLabel}`}
							icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
							label={`${toggleLabel}`}
							onClick={handleToggleExpand}
							showLabel={false}
							size="small"
							variant="tertiary"
						/>
					</div>
				</div>
			</div>
			{isExpanded && (
				<div className={styles.carouselSlot} id={CONTENT_ID}>
					{/* Clips the wide carousel track so it can't stretch the page. */}
					<div className={styles.carouselViewport}>
						<Slider key={windowWidth} ref={sliderRef} {...carouselSettings}>
							{vehicles.map((vehicle) => (
								<div key={vehicle.id} className={styles.slide}>
									<MobilizeVehicleCard
										onClick={() => triggerMobilizeRowVehicleCardClickedEvent(vehicle.id)}
										vehicle={vehicle}
									/>
								</div>
							))}
						</Slider>
					</div>
					<div className={styles.nav}>
						<Button
							aria-label={`${LocalT.translate('prevLabel')}`}
							className={styles.navButton}
							disabled={!canGoPrev}
							icon={ChevronLeftIcon}
							label={`${LocalT.translate('prevLabel')}`}
							onClick={() => sliderRef.current?.slickPrev()}
							showLabel={false}
							size="small"
							variant="tertiary"
						/>
						<Button
							aria-label={`${LocalT.translate('nextLabel')}`}
							className={styles.navButton}
							disabled={!canGoNext}
							icon={ChevronRightIcon}
							label={`${LocalT.translate('nextLabel')}`}
							onClick={() => sliderRef.current?.slickNext()}
							showLabel={false}
							size="small"
							variant="tertiary"
						/>
					</div>
				</div>
			)}
		</section>
	);
};
